import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dice5,
  Plus,
  Trophy,
  Users,
  Calendar,
  Play,
  Square,
  Eye,
  TrendingUp,
  Gift,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const AdminPickNumberGame = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [games, setGames] = useState([]);
  const [currentGame, setCurrentGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEntriesDialog, setShowEntriesDialog] = useState(false);
  const [selectedGameEntries, setSelectedGameEntries] = useState([]);
  const [drawingWinner, setDrawingWinner] = useState(null);

  const [newGame, setNewGame] = useState({
    prize: '',
    duration_hours: 24,
    duration_minutes: 0
  });

  // Check admin access
  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
    } else if (currentUser.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/');
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    if (currentUser?.role === 'admin') {
      fetchData();
    }
  }, [currentUser]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [gamesRes, currentRes] = await Promise.all([
        api.get('/api/pick-number-game/all'),
        api.get('/api/pick-number-game/current')
      ]);
      setGames(gamesRes.data);
      setCurrentGame(currentRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load game data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGame = async () => {
    if (!newGame.prize) {
      toast.error('Please enter a prize');
      return;
    }

    try {
      await api.post('/api/pick-number-game/create', newGame);
      toast.success('Game created successfully!');
      setShowCreateDialog(false);
      setNewGame({ prize: '', duration_hours: 24, duration_minutes: 0 });
      fetchData();
    } catch (error) {
      console.error('Error creating game:', error);
      toast.error(error.response?.data?.detail || 'Failed to create game');
    }
  };

  const handleDrawWinner = async (gameId) => {
    if (!window.confirm('Are you sure you want to draw the winner? This cannot be undone.')) {
      return;
    }

    setDrawingWinner(gameId);

    try {
      const response = await api.post(`/api/pick-number-game/draw-winner/${gameId}`);
      
      toast.success(`Winner drawn! ${response.data.winner_name} won with number ${response.data.winning_number}!`, {
        duration: 8000
      });
      
      fetchData();
    } catch (error) {
      console.error('Error drawing winner:', error);
      toast.error(error.response?.data?.detail || 'Failed to draw winner');
    } finally {
      setDrawingWinner(null);
    }
  };

  const handleViewEntries = async (gameId) => {
    try {
      const response = await api.get(`/api/pick-number-game/entries/${gameId}`);
      setSelectedGameEntries(response.data);
      setShowEntriesDialog(true);
    } catch (error) {
      console.error('Error fetching entries:', error);
      toast.error('Failed to load entries');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-lime-500 text-black">Active</Badge>;
      case 'completed':
        return <Badge className="bg-zinc-600 text-white">Completed</Badge>;
      default:
        return <Badge className="bg-zinc-700 text-white">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-lime-400 animate-spin" />
      </div>
    );
  }

  const activeGames = games.filter(g => g.status === 'active');
  const completedGames = games.filter(g => g.status === 'completed');
  const totalEntries = games.reduce((sum, g) => sum + (g.total_entries || 0), 0);

  return (
    <div className="min-h-screen bg-black text-white py-20">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-2">
              <Dice5 className="inline-block w-10 h-10 mr-3 text-lime-400" />
              Pick a Number - Game Management
            </h1>
            <p className="text-zinc-400">Create, manage, and draw winners</p>
          </div>
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="mt-4 md:mt-0 bg-gradient-to-r from-lime-500 to-lime-600 text-black hover:from-lime-600 hover:to-lime-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create New Game
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-sm">Total Games</p>
                  <p className="text-3xl font-bold text-white">{games.length}</p>
                </div>
                <Dice5 className="w-8 h-8 text-purple-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-sm">Active Games</p>
                  <p className="text-3xl font-bold text-lime-400">{activeGames.length}</p>
                </div>
                <Play className="w-8 h-8 text-lime-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-sm">Total Entries</p>
                  <p className="text-3xl font-bold text-white">{totalEntries}</p>
                </div>
                <Users className="w-8 h-8 text-yellow-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-sm">Completed</p>
                  <p className="text-3xl font-bold text-zinc-400">{completedGames.length}</p>
                </div>
                <Trophy className="w-8 h-8 text-yellow-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Current Game Highlight */}
        {currentGame && (
          <Card className="bg-gradient-to-br from-lime-900/40 to-black border-lime-500 mb-8">
            <CardHeader>
              <CardTitle className="text-lime-400 text-2xl">
                🎮 Current Active Game
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-zinc-400 text-sm">Game #</p>
                  <p className="text-2xl font-bold">{currentGame.game_number}</p>
                </div>
                <div>
                  <p className="text-zinc-400 text-sm">Prize</p>
                  <p className="text-xl font-bold text-lime-400">{currentGame.prize}</p>
                </div>
                <div>
                  <p className="text-zinc-400 text-sm">Total Entries</p>
                  <p className="text-2xl font-bold">{currentGame.total_entries}</p>
                </div>
              </div>

              <div className="flex items-center gap-2 text-sm text-zinc-300">
                <Calendar className="w-4 h-4" />
                Ends: {new Date(currentGame.end_date).toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => handleViewEntries(currentGame.id)}
                  variant="outline"
                  className="border-lime-400 text-lime-400 hover:bg-lime-400 hover:text-black"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  View Entries ({currentGame.total_entries})
                </Button>
                <Button
                  onClick={() => handleDrawWinner(currentGame.id)}
                  disabled={drawingWinner === currentGame.id || currentGame.total_entries === 0}
                  className="bg-yellow-500 text-black hover:bg-yellow-600"
                >
                  <Trophy className="w-4 h-4 mr-2" />
                  {drawingWinner === currentGame.id ? 'Drawing...' : 'Draw Winner'}
                </Button>
                <Button
                  variant="outline"
                  className="border-red-500/60 text-red-300 hover:bg-red-500/15"
                  data-testid="end-game-btn"
                  onClick={async () => {
                    if (!window.confirm('End this game without drawing a winner?\n\nNobody will be picked. The game will be marked "ended" and you can create a new one immediately.')) return;
                    try {
                      const r = await api.post('/api/pick-number-game/admin/end-active');
                      toast.success(`Ended ${r.data.ended} game(s).`);
                      fetchData();
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || 'End game failed');
                    }
                  }}
                >
                  End game (no winner)
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Games List */}
        <Tabs defaultValue="active" className="space-y-6">
          <TabsList className="bg-zinc-900">
            <TabsTrigger value="active">Active ({activeGames.length})</TabsTrigger>
            <TabsTrigger value="completed">Completed ({completedGames.length})</TabsTrigger>
            <TabsTrigger value="all">All Games ({games.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="active">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {activeGames.map(game => (
                <GameCard 
                  key={game.id} 
                  game={game} 
                  onDrawWinner={handleDrawWinner}
                  onViewEntries={handleViewEntries}
                  drawingWinner={drawingWinner}
                />
              ))}
              {activeGames.length === 0 && (
                <Card className="col-span-2 bg-zinc-900 border-zinc-800">
                  <CardContent className="p-12 text-center">
                    <Play className="w-16 h-16 mx-auto mb-4 text-zinc-600" />
                    <h3 className="text-xl font-semibold mb-2">No Active Games</h3>
                    <p className="text-zinc-400 mb-6">Create a new game to get started</p>
                    <Button
                      onClick={() => setShowCreateDialog(true)}
                      className="bg-lime-500 text-black"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create Game
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          <TabsContent value="completed">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {completedGames.map(game => (
                <GameCard 
                  key={game.id} 
                  game={game} 
                  onViewEntries={handleViewEntries}
                />
              ))}
              {completedGames.length === 0 && (
                <Card className="col-span-2 bg-zinc-900 border-zinc-800">
                  <CardContent className="p-12 text-center text-zinc-500">
                    <Trophy className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>No completed games yet</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          <TabsContent value="all">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {games.map(game => (
                <GameCard 
                  key={game.id} 
                  game={game} 
                  onDrawWinner={handleDrawWinner}
                  onViewEntries={handleViewEntries}
                  drawingWinner={drawingWinner}
                />
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* Create Game Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
            <DialogHeader>
              <DialogTitle className="text-lime-400">Create New Game</DialogTitle>
              <DialogDescription className="text-zinc-400">
                Set up a new Pick a Number game for customers
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <Label>Prize</Label>
                <Input
                  value={newGame.prize}
                  onChange={(e) => setNewGame({ ...newGame, prize: e.target.value })}
                  placeholder="e.g., Free Haircut + 100 Bonus Points"
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>

              <div>
                <Label className="mb-2 block">Duration</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-zinc-400">Hours</Label>
                    <Input
                      type="number"
                      value={newGame.duration_hours}
                      onChange={(e) => setNewGame({ ...newGame, duration_hours: parseInt(e.target.value) || 0 })}
                      min="0"
                      max="168"
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-400">Minutes</Label>
                    <Input
                      type="number"
                      value={newGame.duration_minutes}
                      onChange={(e) => setNewGame({ ...newGame, duration_minutes: parseInt(e.target.value) || 0 })}
                      min="0"
                      max="59"
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                </div>
                <p className="text-xs text-zinc-500 mt-2">
                  Game will end in {newGame.duration_hours} hour{newGame.duration_hours !== 1 ? 's' : ''} and {newGame.duration_minutes} minute{newGame.duration_minutes !== 1 ? 's' : ''}
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowCreateDialog(false)}
                className="border-zinc-700 text-white hover:bg-zinc-800"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateGame}
                className="bg-lime-500 text-black hover:bg-lime-600"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Game
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Entries Dialog */}
        <Dialog open={showEntriesDialog} onOpenChange={setShowEntriesDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-lime-400">Game Entries</DialogTitle>
              <DialogDescription className="text-zinc-400">
                {selectedGameEntries.length} total entries
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-2 py-4">
              {selectedGameEntries.map(entry => (
                <div key={entry.id} className="flex items-center justify-between p-3 bg-zinc-800 rounded">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-lime-500 text-black rounded-full flex items-center justify-center font-bold text-lg">
                      {entry.chosen_number}
                    </div>
                    <div>
                      <p className="font-semibold">{entry.user_name}</p>
                      <p className="text-xs text-zinc-400">
                        {new Date(entry.submitted_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

// Game Card Component
const GameCard = ({ game, onDrawWinner, onViewEntries, drawingWinner }) => {
  const isActive = game.status === 'active';
  const hasWinner = game.winning_number !== null;

  return (
    <Card className={`${isActive ? 'bg-zinc-900 border-lime-500' : 'bg-zinc-900 border-zinc-800'}`}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-white">Game #{game.game_number}</CardTitle>
            <div className="flex items-center gap-2 mt-2">
              {game.status === 'active' ? (
                <Badge className="bg-lime-500 text-black">Active</Badge>
              ) : (
                <Badge className="bg-zinc-600 text-white">Completed</Badge>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-lime-400">
              <Gift className="w-6 h-6 inline-block mr-1" />
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="text-zinc-400 text-sm">Prize</p>
          <p className="text-lg font-semibold text-white">{game.prize}</p>
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-zinc-400">Entries</p>
            <p className="text-xl font-bold">{game.total_entries}</p>
          </div>
          <div>
            <p className="text-zinc-400">Ends</p>
            <p className="text-sm">{new Date(game.end_date).toLocaleDateString()}</p>
          </div>
        </div>

        {hasWinner && (
          <div className="bg-yellow-500/20 border border-yellow-500 rounded p-3">
            <p className="text-yellow-400 font-semibold mb-1">🏆 Winner</p>
            <p className="text-white">{game.winner_name}</p>
            <p className="text-zinc-300 text-sm">Winning Number: #{game.winning_number}</p>
          </div>
        )}

        <div className="flex gap-2">
          {onViewEntries && (
            <Button
              onClick={() => onViewEntries(game.id)}
              variant="outline"
              size="sm"
              className="flex-1 border-zinc-700"
            >
              <Eye className="w-4 h-4 mr-1" />
              View Entries
            </Button>
          )}
          {isActive && !hasWinner && onDrawWinner && (
            <Button
              onClick={() => onDrawWinner(game.id)}
              disabled={drawingWinner === game.id || game.total_entries === 0}
              size="sm"
              className="flex-1 bg-yellow-500 text-black hover:bg-yellow-600"
            >
              <Trophy className="w-4 h-4 mr-1" />
              {drawingWinner === game.id ? 'Drawing...' : 'Draw Winner'}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminPickNumberGame;